import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import type { Product } from 'reactApp/types/Billing';
import type { OnResizeCallback } from 'reactApp/ui/IframePages/BuyDisableAds/BuyDisableAds.types';
import { useDisableAdsAnalytics } from 'reactApp/ui/IframePages/BuyDisableAds/hooks/useDisableAdsAnalytics';
import { useDisableAdsTariffs } from 'reactApp/ui/IframePages/BuyDisableAds/hooks/useDisableAdsTariffs';
import { useDisableAdsTitle } from 'reactApp/ui/IframePages/BuyDisableAds/hooks/useDisableAdsTitle';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { TariffsModalContent } from 'reactApp/ui/TariffsModal/TariffsModalContent';
import { sendDwh } from 'reactApp/utils/ga';
import useResizeObserver from 'use-resize-observer/polyfilled';

import styles from './DisableAdsScreen.css';

interface DisableAdsScreenProps {
    titleFromQuery?: string;
    tariffsIdsFromQuery?: string;
    activeTabFromQuery?: string;
    onBuyClick: (product: Product) => void;
    onResize: OnResizeCallback;
    analyticsFromQuery?: string;
}

export const DisableAdsScreen = memo(
    ({ activeTabFromQuery, tariffsIdsFromQuery, titleFromQuery, onBuyClick, onResize, analyticsFromQuery }: DisableAdsScreenProps) => {
        const title = useDisableAdsTitle(titleFromQuery);
        const { tariffs, tariffIds } = useDisableAdsTariffs(tariffsIdsFromQuery);
        const { actionShow, name } = useDisableAdsAnalytics(analyticsFromQuery);

        const [activeTab, setActiveTab] = useState<ETabsName>(activeTabFromQuery === 'm' ? ETabsName.month : ETabsName.year);

        const analyticsTariffList = [...(tariffIds?.year && tariffIds.year), ...(tariffIds?.month && tariffIds.month)];

        const onResizeWrapper = useCallback(
            ({ height = 0 }: { height?: number }) => {
                onResize({ height });
            },
            [onResize]
        );
        const { ref } = useResizeObserver<HTMLDivElement>({ onResize: onResizeWrapper });

        const isFirstEffectCalled = useRef(false);
        useEffect(() => {
            if (!isFirstEffectCalled.current) {
                emitAnalyticEvent(AnalyticEventNames.BUY_DISABLE_ADS_MODAL_SHOWN, { selectedPeriod: activeTab });
                sendDwh({
                    eventCategory: 'tariffs-quota',
                    action: actionShow,
                    dwhData: { selectedPeriod: activeTab, name, tariff_list: analyticsTariffList },
                });
                isFirstEffectCalled.current = true;
            } else {
                emitAnalyticEvent(AnalyticEventNames.BUY_DISABLE_ADS_MODAL_PERIOD_CHANGE, { selectedPeriod: activeTab });
                sendDwh({ eventCategory: 'tariffs-quota', action: 'modal-noads-switch', dwhData: { selectedPeriod: activeTab } });
            }
        }, [activeTab]);

        return (
            <div ref={ref} className={styles.contentRefWrapper}>
                <TariffsModalContent
                    isIframe
                    tariffs={tariffs}
                    yearTariffsCount={tariffIds.year.length}
                    monthTariffsCount={tariffIds.month.length}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    onBuyClick={onBuyClick}
                    title={title}
                />
            </div>
        );
    }
);

DisableAdsScreen.displayName = 'DisableAdsScreen';
